import { TOrganisation } from '@verifime/components';
import { api } from '@verifime/api-definition';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UrlObject } from 'url';
import { useUser } from '../UserContext';

export function useOrganisations(homePath: string) {
  const router = useRouter();

  const { orgId } = router.query;
  const defaultOrgId = typeof orgId === 'string' ? orgId : undefined;
  const [organisations, setOrganisations] = useState<TOrganisation[]>([]);

  const { state: userState } = useUser();
  const hasFetchedOrgs = useRef(false);

  useEffect(() => {
    if (hasFetchedOrgs.current) {
      return;
    }

    if (userState.status === 'loading' || userState.status === 'error') {
      return;
    }

    const abortController = new AbortController();

    const getOrganisations = userState.isAdmin
      ? api
          .getV1organisation({
            queries: { startIndex: 0, lastIndex: 1000, orderBy: 'organisationName' },
            signal: abortController.signal,
          })
          .then((organisationSearchResult) => {
            return organisationSearchResult?.itemList;
          })
      : api.getV1organisationme({ signal: abortController.signal });

    getOrganisations
      .then((orgs) => {
        if (!orgs || orgs.length === 0) {
          return;
        }

        const childOrgIds = orgs
          ?.filter((org) => org.children?.length === 0)
          .map((org) => org.id);

        const availableOrgs: TOrganisation[] = orgs.map((org) => {
          return {
            id: org.id,
            name: org.organisationName,
            children: org.children
              // exclude the children that is not provisioned to the client user
              .filter((org) => childOrgIds.includes(org.id))
              .map((childOrg) => ({
                id: childOrg.id,
                name: childOrg.organisationName,
                children: [] as TOrganisation[],
              })),
          };
        });
        hasFetchedOrgs.current = true;
        setOrganisations(availableOrgs);
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        console.warn('Failed to get organisations', error);
      });

    return () => abortController.abort();
  }, [userState]);

  useEffect(() => {
    if (!defaultOrgId && organisations?.length > 0) {
      const allChildOrgs = organisations.map((org) => org.children).flat();

      if (allChildOrgs.length > 0) {
        router.push(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              orgId: allChildOrgs[0].id,
            },
          },
          undefined,
          { shallow: true },
        );
      }
    }
  }, [defaultOrgId, organisations, router]);

  const handleChangeOrgId = useCallback(
    (currentOrgId: string) => {
      router.push({
        pathname: homePath,
        query: {
          orgId: currentOrgId,
        },
      });
    },
    [router, homePath],
  );

  const navItemHrefProcessor = useCallback(
    (href: UrlObject) => {
      return {
        ...href,
        query: {
          orgId: defaultOrgId,
        },
      };
    },
    [defaultOrgId],
  );

  return { organisations, defaultOrgId, handleChangeOrgId, navItemHrefProcessor };
}
